import { useCallback, useEffect, useRef, type ReactNode } from "react";
import type { ModuleDefinition } from "../registry";
import { ModuleTile } from "./ModuleTile";
import styles from "./ModulesTabPanel.module.css";

/** Auto-dismiss delay for the pin-limit popup. Driven by setTimeout (not CSS)
 *  for the same Office-WebView reason documented in NotificationToaster. */
const PIN_LIMIT_TOAST_MS = 4000;

interface PinLimitToastProps {
  readonly maxPins: number;
  readonly onDismiss: () => void;
}

/** Transient popup shown when the user tries to pin past the ceiling (AIVA-7299). */
function PinLimitToast({ maxPins, onDismiss }: PinLimitToastProps) {
  const timerRef = useRef<number | null>(null);

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      globalThis.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    timerRef.current = globalThis.setTimeout(onDismiss, PIN_LIMIT_TOAST_MS);
    return clear;
  }, [onDismiss, clear]);

  return (
    <div className={styles.limitToastWrap}>
      {/* <output> carries an implicit role="status" + aria-live="polite", so the
          message is announced without an explicit role. */}
      <output className={styles.limitToast} onMouseEnter={clear}>
        <span className={styles.limitToastText}>
          You can pin a maximum of {maxPins} modules. Unpin one to add another.
        </span>
        <button
          type="button"
          className={styles.limitToastClose}
          aria-label="Dismiss message"
          onClick={onDismiss}
        >
          &times;
        </button>
      </output>
    </div>
  );
}

interface ModulesTabPanelProps {
  readonly modules: ModuleDefinition[];
  readonly pinnedModuleIds: string[];
  readonly onTogglePin: (moduleId: string) => void;
  readonly onOpenModule: (moduleId: string) => void;
  /**
   * Module ids the backend lists but whose credentials endpoint returned no
   * authConfig. Tiles in this set render with a "Setup required" badge so the
   * user knows to contact an admin instead of clicking into a dead module.
   */
  readonly setupRequiredIds?: ReadonlySet<string>;
  /** Maximum modules a user may pin — used to phrase the limit message. */
  readonly maxPins?: number;
  /**
   * True when a pin attempt was rejected because {@link maxPins} was already
   * reached. Pops a transient message telling the user to unpin first (AIVA-7299).
   */
  readonly pinLimitReached?: boolean;
  /** Clears {@link pinLimitReached} when the popup auto-dismisses or is closed. */
  readonly onDismissPinLimit?: () => void;
  /** Rendered below the module grid — the "AI and compliance" section (AIVA-6639). */
  readonly footerContent?: ReactNode;
}

export function ModulesTabPanel({
  modules,
  pinnedModuleIds,
  onTogglePin,
  onOpenModule,
  setupRequiredIds,
  maxPins = 2,
  pinLimitReached = false,
  onDismissPinLimit,
  footerContent,
}: Readonly<ModulesTabPanelProps>) {
  const isEmpty = modules.length === 0;
  const hasSetupGap = Boolean(setupRequiredIds && setupRequiredIds.size > 0);
  return (
    <div className={styles.modulesPanel}>
      <h2 className={styles.title}>AiVA Modules</h2>
      {isEmpty ? (
        <p className={styles.empty}>
          Modules unavailable. Check your connection and try again.
        </p>
      ) : (
        <>
          <p className={styles.hint}>
            Chat and Actions are always pinned — choose up to <strong>2 more</strong>
          </p>
          {hasSetupGap && (
            <output className={styles.setupBanner}>
              Some modules are not configured yet. Contact your administrator to enable them.
            </output>
          )}
          <div className={styles.grid}>
            {modules.map((mod) => (
              <ModuleTile
                key={mod.id}
                module={mod}
                isPinned={pinnedModuleIds.includes(mod.id)}
                onTogglePin={() => onTogglePin(mod.id)}
                onOpen={() => onOpenModule(mod.id)}
                setupRequired={setupRequiredIds?.has(mod.id) ?? false}
              />
            ))}
          </div>
        </>
      )}
      {footerContent}
      {pinLimitReached && (
        <PinLimitToast maxPins={maxPins} onDismiss={onDismissPinLimit ?? (() => {})} />
      )}
    </div>
  );
}
